import { useEffect, useState } from 'react';

/** "Quality education · Skills for life" → ["Quality education", "Skills for life"]. */
export const splitMotto = (motto: string): string[] =>
  motto
    .split('·')
    .map((piece) => piece.trim())
    .filter(Boolean);

type Options = {
  /** Delay before each typed character. */
  readonly typeMs?: number;
  /** Delay before each erased character. */
  readonly deleteMs?: number;
  /** How long a fully typed phrase stays before erasing starts. */
  readonly holdMs?: number;
  /** Pause on an empty line before the next phrase starts. */
  readonly gapMs?: number;
  /** No animation (reduced motion): the phrases joined, at once. */
  readonly disabled?: boolean;
};

type Phase = 'typing' | 'holding' | 'deleting' | 'gap';

type State = { readonly index: number; readonly length: number; readonly phase: Phase };

type Timing = Required<Omit<Options, 'disabled'>>;

/** One step of the machine: the next state, how long until it, and the text to show (if changed). */
export const advance = (
  state: State,
  phrases: readonly string[],
  { typeMs, deleteMs, holdMs, gapMs }: Timing
): { next: State; delay: number; text?: string } => {
  const phrase = phrases[state.index] ?? '';

  switch (state.phase) {
    case 'typing': {
      const length = state.length + 1;
      const done = length >= phrase.length;
      return {
        next: { ...state, length, phase: done ? 'holding' : 'typing' },
        delay: done ? holdMs : typeMs,
        text: phrase.slice(0, length),
      };
    }
    case 'holding': {
      return { next: { ...state, phase: 'deleting' }, delay: deleteMs };
    }
    case 'deleting': {
      const length = state.length - 1;
      const empty = length <= 0;
      return {
        next: { ...state, length, phase: empty ? 'gap' : 'deleting' },
        delay: empty ? gapMs : deleteMs,
        text: phrase.slice(0, length),
      };
    }
    case 'gap': {
      return {
        next: { index: (state.index + 1) % phrases.length, length: 0, phase: 'typing' },
        delay: typeMs,
      };
    }
  }
};

/**
 * Types each phrase, holds it, erases it, then moves to the next, looping. The state machine is the
 * pure `advance`; one timer chain per effect run drives it, so the timing does not depend on React
 * re-render scheduling, and aborting the chain on cleanup stops it.
 */
export const useTypewriter = (
  phrases: readonly string[],
  { typeMs = 70, deleteMs = 35, holdMs = 1800, gapMs = 400, disabled = false }: Options = {}
): string => {
  const [text, setText] = useState('');
  const key = phrases.join('\u0000');

  useEffect(() => {
    if (disabled || phrases.length === 0) {
      return;
    }

    const timing = { typeMs, deleteMs, holdMs, gapMs };
    const controller = new AbortController();

    const run = (state: State) => {
      if (controller.signal.aborted) {
        return;
      }
      const { next, delay, text: shown } = advance(state, phrases, timing);
      if (shown !== undefined) {
        setText(shown);
      }
      setTimeout(() => {
        run(next);
      }, delay);
    };

    setText('');
    setTimeout(() => {
      run({ index: 0, length: 0, phase: 'typing' });
    }, typeMs);

    return () => {
      controller.abort();
    };
    // `key` stands for `phrases`: a new array with the same words must not restart the animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, disabled, typeMs, deleteMs, holdMs, gapMs]);

  return disabled ? phrases.join(' · ') : text;
};
